import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { PackagePlus, Plus, RefreshCw, Search } from "lucide-react";
import { fromJS } from "immutable";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { useQuery } from "@tanstack/react-query";
import { getBaseUrl } from "../../api/api";
import { SmallLoading } from "../../main/Loading";
import { getAppLink } from "../../api/AppLinkApi";
import RuleAction from "../../actions/RuleAction";
import { getCurrentCanvaMe, getDesignPages, getDesigns } from "../../api/CanvaApi";
import ProfileAvatar from "../../rules/components/ProfileAvatar";
import {
  PLATFORM_BACKGROUND_COLOR,
  PLATFORM_IMAGE,
  PLATFORM_TEXT_COLOR_HEX,
  PLATFORM_TITLE,
} from "../constants/platform";
import CanvaPlus from "../../icons/CanvaPlus";
import CanvaDesignPage from "./CanvaDesignPage";

interface CanvaDesignProps {
  projectId: string;
  onExportImage: (filename: string, file: File) => Promise<void>;
  navigateToUrl: (url: string) => void;
  className?: string;
}

const CanvaDesignProfile = () => {
  const dispatch = useDispatch();
  const platformProfiles = useSelector((state: any) => state.ruleReducer.get("platformProfiles"));
  const [loading, setLoading] = useState(false);
  const profile = platformProfiles?.get?.("canva")?.toJS?.() || null;

  useEffect(() => {
    if (platformProfiles?.get?.("canva")) {
      return;
    }
    setLoading(true);
    getCurrentCanvaMe()
      .then((data) => {
        dispatch(
          RuleAction.setPlatformProfile({
            platformId: "canva",
            profile: {
              picture: "",
              name: data?.profile?.display_name,
              username: "",
            },
          })
        );
      })
      .catch(() => { })
      .finally(() => setLoading(false));
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {loading ? (
        <div className="h-8 w-8 rounded-full bg-gray-200 animate-pulse" />
      ) : (
        <ProfileAvatar picture={profile?.picture} name={profile?.name} size="sm" />
      )}
    </>
  );
};

const CanvaDesign = ({ projectId, onExportImage, navigateToUrl, className }: CanvaDesignProps) => {
  const { t } = useTranslation();

  const [isLoadingCanvaConnection, setIsLoadingCanvaConnection] = useState(true);
  const [isCanvaConnected, setIsCanvaConnected] = useState(false);

  const [designId, setDesignId] = useState("");
  const [designTitle, setDesignTitle] = useState("");
  const [searchText, setSearchText] = useState<string>("");
  const [debouncedSearchText, setDebouncedSearchText] = useState<string>(searchText);

  const {
    data: designs,
    isLoading: isLoadingCanvaDesigns,
    refetch: refetchCanvaDesigns,
    isFetching: isFetchingCanvaDesigns,
  } = useQuery({
    queryKey: ["canva-designs", debouncedSearchText],
    queryFn: async () => {
      return await getDesigns(debouncedSearchText);
    },
    enabled: !designId,
    staleTime: 60 * 1000, // cache 60s
  });

  const { data: designPages, isLoading: isLoadingDesignPages } = useQuery({
    queryKey: ["canva-design-pages", designId],
    queryFn: async () => {
      return await getDesignPages(designId);
    },
    enabled: !!designId,
    staleTime: 60 * 1000, // cache 60s
  });

  useEffect(() => {
    loadAppData();
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 800);
    return () => {
      clearTimeout(handler);
    };
  }, [searchText]);

  const loadAppData = () => {
    getAppLink("canva")
      .then((data) => {
        setIsCanvaConnected(data.connected || false);
      })
      .finally(() => {
        setIsLoadingCanvaConnection(false);
      });
  };

  const renderDesign = () => {
    if (isLoadingCanvaDesigns) {
      return (
        <div className="w-full h-56 flex items-center justify-center">
          <SmallLoading />
        </div>
      );
    }
    const canvaDesigns = fromJS(designs?.items || []);
    if (canvaDesigns.size === 0) {
      return <div className="w-full h-56 pt-2 text-sm text-gray-600 text-center">{t("No design")}</div>;
    }
    return (
      <div className="w-full h-56 overflow-y-auto p-2">
        <div className="grid grid-cols-5 gap-2">
          {canvaDesigns.map((item) => (
            <div
              key={item.get("id")}
              className="w-20 h-full flex flex-col rounded-xl border border-gray-200 overflow-hidden bg-white cursor-pointer"
              onClick={() => {
                setDesignId(item.get("id"));
                setDesignTitle(item.get("title") || "");
              }}
            >
              <div className="w-full h-full aspect-[4/5] overflow-hidden">
                <img
                  src={item.getIn(["thumbnail", "url"])}
                  alt={item.get("title")}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="px-2 py-1.5 border-t border-gray-100 bg-white">
                <p
                  title={item.get("title")}
                  className="text-[11px] font-medium text-gray-700 truncate"
                >
                  {item.get("title") || t("Untitled")}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderDesignPage = () => {
    if (isLoadingDesignPages) {
      return (
        <div className="w-full h-56 flex items-center justify-center">
          <SmallLoading />
        </div>
      );
    }
    const canvaDesignPages = fromJS(designPages?.items || []);
    if (canvaDesignPages.size === 0) {
      return <div className="w-full h-56 pt-2 text-sm text-gray-600 text-center">{t("No page")}</div>;
    }
    return (
      <div className="w-full h-56 overflow-y-auto p-2">
        <div className="grid grid-cols-5 gap-2">
          {canvaDesignPages.map((item) => (
            <CanvaDesignPage
              key={item.get("index")}
              designId={designId}
              designTitle={designTitle}
              item={item}
              onExportImage={onExportImage}
              className="w-20"
            />
          ))}
        </div>
      </div>
    );
  };

  const renderPlatformDesign = (platform) => {
    let url;
    switch (platform) {
      case "instagram":
        url = "https://www.canva.com/design?create&type=TAEby5mLXEk&category=tACFajEYUAM&ui=e30";
        break;
      case "facebook":
        url = "https://www.canva.com/design?create&type=TACQ-rUo-uE&category=tACFavw5Wlg&ui=e30";
        break;
      case "linkedin":
        url = "https://www.canva.com/design?create&type=TAEHQ-OPEx0&category=tAEL9-BMBAg&ui=e30";
        break;
      case "twitter":
        url = "https://www.canva.com/design?create&type=TAD-2D3JRN4&category=tACFagP1mXg&ui=e30";
        break;
      case "threads":
        url = "https://www.canva.com/design?create&type=TAFoNQA2s7M&category=tAFoNX-UKHw&ui=e30";
        break;
    }
    return (
      <div
        className="w-full h-full py-1 border border-gray-200 rounded-lg bg-gray-50 space-y-1 cursor-pointer"
        onClick={() => {
          if (url) window.open(url, "_blank");
        }}
      >
        <div className="flex items-center justify-center gap-1">
          <div className="p-1">
            <img
              src={`${getBaseUrl()}${PLATFORM_IMAGE[platform]}`}
              className="h-5"
              alt={platform}
            />
          </div>
          <div className={`w-16 h-2 rounded-xl ${PLATFORM_BACKGROUND_COLOR[platform]}`}></div>
        </div>
        <div
          className={`w-full h-10 flex items-center justify-center ${PLATFORM_BACKGROUND_COLOR[platform]}`}
        >
          <CanvaPlus size={12} color={PLATFORM_TEXT_COLOR_HEX[platform]} />
        </div>
        <div className="px-2 text-xs font-semibold text-center">
          {`${PLATFORM_TITLE[platform]} Post`}
        </div>
      </div>
    );
  };

  if (isLoadingCanvaConnection) {
    return (
      <div className="h-20 flex items-center justify-center">
        <SmallLoading />
      </div>
    );
  }

  if (!isCanvaConnected) {
    return (
      <div className="p-4 flex flex-col items-center justify-center">
        <div>
          <img
            src={`${getBaseUrl()}/static/images/canva.png`}
            className="h-8 object-contain"
            alt="Canva logo"
          />
        </div>
        <div className="pt-2 w-50 text-center text-sm text-gray-600">
          {t("Please connect your Canva account to use this feature")}
        </div>
        <div className="pt-4">
          <div
            className="px-2 py-1 flex items-center gap-2 rounded bg-blue-600 text-white text-xs font-semibold cursor-pointer"
            onClick={() => {
              navigateToUrl(`/workspaces/${projectId}/settings/apps?key=Apps&appId=canva`);
            }}
          >
            <PackagePlus className="w-4 h-4" />
            <span>{t("Connect with Canva")}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${className} flex flex-col`}>
      <div className="px-4 py-2 flex items-center justify-between border-b border-gray-100">
        <div className="flex items-center gap-4">
          <img
            src={`${getBaseUrl()}/static/images/canva.png`}
            className="h-6 object-contain"
            alt="Canva"
          />
          {designId ? (
            <div className="text-sm font-semibold text-gray-800">{t("Pages")}</div>
          ) : (
            <div className="text-sm font-semibold text-gray-800">{t("Designs")}</div>
          )}
        </div>
        <CanvaDesignProfile />
      </div>
      {designId ? (
        <div className="pt-2">
          <div className="mb-1 px-4 flex items-center justify-between">
            <div className="flex items-center gap-2 rounded-md border border-gray-200 px-2 py-1 cursor-pointer">
              <Search className="h-4 w-4 text-gray-500" />
              <input
                disabled
                placeholder="Search"
                className="w-full bg-transparent text-sm outline-none placeholder:text-gray-500 disabled:text-gray-400"
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
            <div
              className="px-2 py-1 hover:bg-gray-100 rounded text-sm cursor-pointer"
              onClick={() => {
                setDesignId("");
              }}
            >
              {t("Back")}
            </div>
          </div>
          {renderDesignPage()}
        </div>
      ) : (
        <div className="pt-2">
          <div className="mb-1 px-4 flex items-center justify-between">
            <div className="flex items-center gap-2 rounded-md border border-gray-200 px-2 py-1 cursor-pointer">
              <Search className="h-4 w-4 text-gray-500" />
              <input
                autoFocus
                placeholder="Search"
                className="w-full bg-transparent text-sm outline-none placeholder:text-gray-500"
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-1">
              <button
                className="p-1 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors cursor-pointer"
                onClick={() => refetchCanvaDesigns()}
                title={t("Refresh")}
                disabled={isFetchingCanvaDesigns}
              >
                <RefreshCw
                  className={`w-4 h-4 text-gray-400 ${isFetchingCanvaDesigns ? "animate-spin" : ""}`}
                />
              </button>
              <Popover className="relative">
                <PopoverButton
                  className="p-1 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors cursor-pointer outline-none"
                  title={t("Create Design")}
                >
                  <Plus className="w-5 h-5 text-gray-400" />
                </PopoverButton>
                <PopoverPanel
                  anchor="bottom end"
                  className="z-9999 grid grid-cols-3 gap-4 p-4 bg-white rounded-md border border-gray-300 shadow-md"
                >
                  {renderPlatformDesign("instagram")}
                  {renderPlatformDesign("facebook")}
                  {renderPlatformDesign("linkedin")}
                  {renderPlatformDesign("twitter")}
                  {renderPlatformDesign("threads")}
                </PopoverPanel>
              </Popover>
            </div>
          </div>
          {renderDesign()}
        </div>
      )}
    </div>
  );
};

export default CanvaDesign;
